import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import ConfirmModal from '../common/ConfirmModal.tsx';
import {
  Users,
  UserPlus,
  Shield,
  UserCheck,
  UserX,
  Edit,
  Trash2,
  KeyRound,
  Mail,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  Info,
  Lock,
  User as UserIcon,
  ShieldAlert,
  FileEdit,
  PenTool,
  Upload,
  Camera,
} from 'lucide-react';
import { AppUser, UserRole } from '../../types.ts';

export default function UserManager() {
  const { users, user: currentUser, addUser, editUser, removeUser, showToast, settings } = useApp();
  const primaryColor = settings?.theme.primaryColor || '#059669';

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [passwordModalUser, setPasswordModalUser] = useState<AppUser | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    password: '',
    role: 'editor' as UserRole,
    status: 'active' as 'active' | 'inactive',
    avatar: '',
    bio: '',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    role: 'editor' as UserRole,
    status: 'active' as 'active' | 'inactive',
    avatar: '',
    bio: '',
    newPassword: '',
  });

  const [submitting, setSubmitting] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, isEdit: boolean) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast('Ukuran foto terlalu besar. Maksimal 5MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        if (isEdit) {
          setEditFormData((prev) => ({ ...prev, avatar: result }));
        } else {
          setFormData((prev) => ({ ...prev, avatar: result }));
        }
        showToast('Foto profil/avatar berhasil diunggah!', 'success');
      }
    };
    reader.readAsDataURL(file);
  };

  // Filtered users
  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const adminCount = users.filter((u) => u.role === 'admin').length;
  const editorCount = users.filter((u) => u.role === 'editor').length;
  const authorCount = users.filter((u) => u.role === 'author').length;
  const userRoleCount = users.filter((u) => u.role === 'user' || !u.role).length;
  const activeCount = users.filter((u) => u.status === 'active').length;

  const handleOpenAddModal = () => {
    setFormData({
      username: '',
      name: '',
      email: '',
      password: '',
      role: 'editor',
      status: 'active',
      avatar: '',
      bio: '',
    });
    setIsAddModalOpen(true);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username.trim() || !formData.password || !formData.name.trim() || !formData.email.trim()) {
      showToast('Harap lengkapi semua bidang wajib bertanda bintang (*)', 'error');
      return;
    }

    setSubmitting(true);
    const success = await addUser({
      ...formData,
      avatar:
        formData.avatar ||
        `https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80`,
    });
    setSubmitting(false);

    if (success) {
      setIsAddModalOpen(false);
    }
  };

  const handleOpenEditModal = (targetUser: AppUser) => {
    setEditingUser(targetUser);
    setEditFormData({
      name: targetUser.name,
      email: targetUser.email,
      role: targetUser.role,
      status: targetUser.status,
      avatar: targetUser.avatar || '',
      bio: targetUser.bio || '',
      newPassword: '',
    });
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    if (!editFormData.name.trim() || !editFormData.email.trim()) {
      showToast('Nama dan email tidak boleh kosong', 'error');
      return;
    }

    setSubmitting(true);
    const payload: any = {
      name: editFormData.name,
      email: editFormData.email,
      role: editFormData.role,
      status: editFormData.status,
      avatar: editFormData.avatar,
      bio: editFormData.bio,
    };
    if (editFormData.newPassword && editFormData.newPassword.trim()) {
      payload.password = editFormData.newPassword.trim();
    }

    const success = await editUser(editingUser.id, payload);
    setSubmitting(false);

    if (success) {
      setEditingUser(null);
    }
  };

  const [userToDelete, setUserToDelete] = useState<AppUser | null>(null);

  const handleDeleteUser = (targetUser: AppUser) => {
    if (targetUser.id === currentUser?.id) {
      showToast('Anda tidak dapat menghapus akun Anda sendiri saat sedang masuk.', 'error');
      return;
    }
    setUserToDelete(targetUser);
  };

  const executeDeleteUser = async () => {
    if (!userToDelete) return;
    await removeUser(userToDelete.id);
    setUserToDelete(null);
  };

  const handleToggleStatus = async (targetUser: AppUser) => {
    const newStatus = targetUser.status === 'active' ? 'inactive' : 'active';
    await editUser(targetUser.id, { status: newStatus });
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-slate-800 tracking-tight">Manajemen Pengguna & Peran (User Roles)</h2>
            <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold rounded-full border border-blue-200">
              Multi-User Role
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Tambah dan kelola hak akses tim redaksi. Tersedia 2 level peran: <strong>Admin (Akses Penuh)</strong> dan <strong>User / Staf (Peran di bawah Admin untuk mengelola konten)</strong>.
          </p>
        </div>

        <button
          onClick={handleOpenAddModal}
          type="button"
          className="px-4 py-2.5 text-xs font-bold text-white rounded-xl shadow-md hover:opacity-95 transition-all flex items-center gap-2 cursor-pointer shrink-0"
          style={{ backgroundColor: primaryColor }}
        >
          <UserPlus className="w-4 h-4" />
          <span>Tambah Pengguna Baru</span>
        </button>
      </div>

      {/* Stats Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-slate-200 flex items-center justify-center text-slate-700 font-bold shrink-0">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-slate-400 block uppercase">Total Akun</span>
            <span className="text-base font-black text-slate-800">{users.length} Akun</span>
          </div>
        </div>

        <div className="p-3.5 bg-emerald-50/50 rounded-2xl border border-emerald-200/70 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center text-white font-bold shrink-0">
            <Shield className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-emerald-700 block uppercase">Admin Utama</span>
            <span className="text-base font-black text-emerald-900">{adminCount} Akun</span>
          </div>
        </div>

        <div className="p-3.5 bg-purple-50/50 rounded-2xl border border-purple-200/70 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-600 flex items-center justify-center text-white font-bold shrink-0">
            <FileEdit className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-purple-700 block uppercase">Redaktur Konten</span>
            <span className="text-base font-black text-purple-900">{editorCount} Akun</span>
          </div>
        </div>

        <div className="p-3.5 bg-amber-50/50 rounded-2xl border border-amber-200/70 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-600 flex items-center justify-center text-white font-bold shrink-0">
            <PenTool className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-amber-700 block uppercase">Penulis Konten</span>
            <span className="text-base font-black text-amber-900">{authorCount} Akun</span>
          </div>
        </div>

        <div className="p-3.5 bg-blue-50/50 rounded-2xl border border-blue-200/70 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold shrink-0">
            <UserIcon className="w-4 h-4" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-blue-700 block uppercase">Staf Konten</span>
            <span className="text-base font-black text-blue-900">{userRoleCount} Akun</span>
          </div>
        </div>
      </div>

      {/* Role Explanation Card */}
      <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-xs text-slate-700 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="flex items-start gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 mt-0.5 font-bold">
            <Shield className="w-3.5 h-3.5" />
          </div>
          <div>
            <span className="font-bold text-slate-800 block text-xs">Administrator</span>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              Akses penuh ke semua menu: Tema, logo, footer, navigasi, keamanan, dan pengaturan sistem.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-purple-600 text-white flex items-center justify-center shrink-0 mt-0.5 font-bold">
            <FileEdit className="w-3.5 h-3.5" />
          </div>
          <div>
            <span className="font-bold text-slate-800 block text-xs">Redaktur Konten</span>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              Hak kelola penuh atas naskah artikel berita, produk UMKM, portofolio, galeri media & komentar.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0 mt-0.5 font-bold">
            <PenTool className="w-3.5 h-3.5" />
          </div>
          <div>
            <span className="font-bold text-slate-800 block text-xs">Penulis Konten</span>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              Khusus menulis naskah artikel berita, mengunggah dokumentasi foto/video, dan mencatat proyek.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0 mt-0.5 font-bold">
            <UserIcon className="w-3.5 h-3.5" />
          </div>
          <div>
            <span className="font-bold text-slate-800 block text-xs">Staf Konten</span>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              Kontributor entri konten dasar & penyuntingan artikel/produk tanpa hak akses administrasi.
            </p>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cari berdasarkan nama, username, atau email..."
            className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
          />
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          <span className="text-xs font-bold text-slate-500 shrink-0">Filter Peran:</span>
          <div className="flex rounded-xl bg-slate-100 p-1 border border-slate-200 shrink-0">
            {(['all', 'admin', 'editor', 'author', 'user'] as const).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRoleFilter(r)}
                className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  roleFilter === r
                    ? 'bg-white text-slate-800 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {r === 'all'
                  ? 'Semua'
                  : r === 'admin'
                  ? 'Admin'
                  : r === 'editor'
                  ? 'Redaktur'
                  : r === 'author'
                  ? 'Penulis'
                  : 'Staf'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-xs">
        <table className="w-full text-left text-xs text-slate-600">
          <thead className="bg-slate-50 text-slate-700 font-bold border-b border-slate-200 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="py-3 px-4">Pengguna</th>
              <th className="py-3 px-4">Username</th>
              <th className="py-3 px-4">Deskripsi Biografi</th>
              <th className="py-3 px-4">Tingkat Peran</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Aktivitas Terakhir</th>
              <th className="py-3 px-4 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-slate-400">
                  Tidak ada data pengguna yang sesuai dengan pencarian.
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => {
                const isCurrent = currentUser?.id === u.id || currentUser?.username === u.username;
                return (
                  <tr key={u.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={u.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80'}
                          alt={u.name}
                          className="w-9 h-9 rounded-full object-cover border border-slate-200 shadow-xs shrink-0"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                        <div>
                          <div className="font-bold text-slate-800 flex items-center gap-1.5">
                            <span>{u.name}</span>
                            {isCurrent && (
                              <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded-md font-bold">
                                Anda
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" />
                            <span>{u.email}</span>
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 font-mono font-bold text-slate-700">
                      @{u.username}
                    </td>

                    <td className="py-3.5 px-4 max-w-xs">
                      {u.bio ? (
                        <p className="text-[11px] text-slate-600 line-clamp-2 leading-tight">
                          {u.bio}
                        </p>
                      ) : (
                        <span className="text-[11px] text-slate-400 italic">
                          - Belum diisi -
                        </span>
                      )}
                    </td>

                    <td className="py-3.5 px-4">
                      {u.role === 'admin' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                          <Shield className="w-3 h-3 text-emerald-700" />
                          <span>Admin Utama</span>
                        </span>
                      ) : u.role === 'editor' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-100 text-purple-800 border border-purple-200">
                          <FileEdit className="w-3 h-3 text-purple-700" />
                          <span>Redaktur Konten</span>
                        </span>
                      ) : u.role === 'author' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                          <PenTool className="w-3 h-3 text-amber-700" />
                          <span>Penulis Konten</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800 border border-blue-200">
                          <UserIcon className="w-3 h-3 text-blue-700" />
                          <span>Staf Konten</span>
                        </span>
                      )}
                    </td>

                    <td className="py-3.5 px-4">
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(u)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold cursor-pointer transition-all ${
                          u.status === 'active'
                            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                            : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200'
                        }`}
                        title="Klik untuk mengubah status aktif/nonaktif"
                      >
                        {u.status === 'active' ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            <span>Aktif</span>
                          </>
                        ) : (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                            <span>Nonaktif</span>
                          </>
                        )}
                      </button>
                    </td>

                    <td className="py-3.5 px-4 text-[11px] text-slate-500">
                      {u.lastLogin ? (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span>{new Date(u.lastLogin).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">Belum pernah login</span>
                      )}
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(u)}
                          className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                          title="Edit Akun & Password"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteUser(u)}
                          disabled={isCurrent}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          title={isCurrent ? 'Tidak dapat menghapus akun Anda sendiri' : 'Hapus Pengguna'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL 1: TAMBAH PENGGUNA BARU */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                  <UserPlus className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-800">Tambah Pengguna Baru</h3>
                  <p className="text-xs text-slate-500">Buat akun untuk administrator atau staf redaksi.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Nama Lengkap <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Contoh: Rian Pratama"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Email Pengguna <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="nama@nusantara.id"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Username Login <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="huruf_kecil_tanpa_spasi"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s+/g, '') })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800 font-mono"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Password Akun <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    placeholder="Minimal 6 karakter"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                  />
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-2">
                  Pilih Tingkat Peran (Role) <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div
                    onClick={() => setFormData({ ...formData, role: 'admin' })}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                      formData.role === 'admin'
                        ? 'bg-emerald-50 border-emerald-500 ring-2 ring-emerald-500/20'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Shield className={`w-4 h-4 ${formData.role === 'admin' ? 'text-emerald-700' : 'text-slate-400'}`} />
                      <span className="text-xs font-black text-slate-800">Administrator Utama</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Akses penuh ke semua menu, pengaturan sistem, tema, logo, dan manajemen pengguna.
                    </p>
                  </div>

                  <div
                    onClick={() => setFormData({ ...formData, role: 'editor' })}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                      formData.role === 'editor'
                        ? 'bg-purple-50 border-purple-500 ring-2 ring-purple-500/20'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileEdit className={`w-4 h-4 ${formData.role === 'editor' ? 'text-purple-700' : 'text-slate-400'}`} />
                      <span className="text-xs font-black text-slate-800">Redaktur Konten</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Peran di bawah admin: Mengedit & mengelola artikel, produk, portofolio, galeri & komentar.
                    </p>
                  </div>

                  <div
                    onClick={() => setFormData({ ...formData, role: 'author' })}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                      formData.role === 'author'
                        ? 'bg-amber-50 border-amber-500 ring-2 ring-amber-500/20'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <PenTool className={`w-4 h-4 ${formData.role === 'author' ? 'text-amber-700' : 'text-slate-400'}`} />
                      <span className="text-xs font-black text-slate-800">Penulis Konten</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Peran di bawah admin: Menulis naskah berita & mengunggah dokumentasi media.
                    </p>
                  </div>

                  <div
                    onClick={() => setFormData({ ...formData, role: 'user' })}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                      formData.role === 'user'
                        ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-500/20'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <UserIcon className={`w-4 h-4 ${formData.role === 'user' ? 'text-blue-700' : 'text-slate-400'}`} />
                      <span className="text-xs font-black text-slate-800">Staf Konten</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Peran di bawah admin: Kontributor & entri data konten dasar.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 flex items-center justify-between mb-1">
                  <span>Biografi & Catatan Penulis:</span>
                  <span className="text-[10px] text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">Tampil di Bawah Artikel & Profil</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="Contoh: Praktisi profesional dan penulis artikel dengan fokus pada inovasi, manajemen, dan pengembangan bisnis..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Jika diisi, teks biografi ini akan ditampilkan pada kotak profil penulis di bagian bawah artikel yang ditulis oleh akun ini.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 block">
                  Foto Profil / Avatar Penulis
                </label>
                <div className="flex items-center gap-3">
                  <img
                    src={formData.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80'}
                    alt="Preview"
                    className="w-12 h-12 rounded-full object-cover border-2 border-slate-200 shadow-2xs shrink-0"
                  />
                  <div className="flex-1 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <label className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl cursor-pointer border border-slate-300 flex items-center gap-1.5 transition-colors">
                        <Upload className="w-3.5 h-3.5 text-slate-600" />
                        <span>Unggah Foto File...</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, false)}
                        />
                      </label>
                    </div>
                    <input
                      type="text"
                      placeholder="Atau paste URL foto (https://...)"
                      value={formData.avatar}
                      onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                      className="w-full px-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 text-xs font-bold text-white rounded-xl shadow-md hover:opacity-95 transition-all cursor-pointer disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {submitting ? 'Menyimpan...' : 'Simpan Pengguna'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: EDIT PENGGUNA & GANTI PASSWORD */}
      {editingUser && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center font-bold">
                  <Edit className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-800">Edit Pengguna: @{editingUser.username}</h3>
                  <p className="text-xs text-slate-500">Perbarui profil, peran, status, atau atur kata sandi baru.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingUser(null)}
                className="text-slate-400 hover:text-slate-700 text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Nama Lengkap <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Email Pengguna <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                  />
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-2">
                  Tingkat Peran (Role)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div
                    onClick={() => setEditFormData({ ...editFormData, role: 'admin' })}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                      editFormData.role === 'admin'
                        ? 'bg-emerald-50 border-emerald-500 ring-2 ring-emerald-500/20'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Shield className={`w-4 h-4 ${editFormData.role === 'admin' ? 'text-emerald-700' : 'text-slate-400'}`} />
                      <span className="text-xs font-black text-slate-800">Administrator Utama</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Akses penuh (Semua menu & administrasi).
                    </p>
                  </div>

                  <div
                    onClick={() => setEditFormData({ ...editFormData, role: 'editor' })}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                      editFormData.role === 'editor'
                        ? 'bg-purple-50 border-purple-500 ring-2 ring-purple-500/20'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileEdit className={`w-4 h-4 ${editFormData.role === 'editor' ? 'text-purple-700' : 'text-slate-400'}`} />
                      <span className="text-xs font-black text-slate-800">Redaktur Konten</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Di bawah admin (Artikel, produk, portofolio & galeri).
                    </p>
                  </div>

                  <div
                    onClick={() => setEditFormData({ ...editFormData, role: 'author' })}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                      editFormData.role === 'author'
                        ? 'bg-amber-50 border-amber-500 ring-2 ring-amber-500/20'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <PenTool className={`w-4 h-4 ${editFormData.role === 'author' ? 'text-amber-700' : 'text-slate-400'}`} />
                      <span className="text-xs font-black text-slate-800">Penulis Konten</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Di bawah admin (Naskah berita & dokumentasi).
                    </p>
                  </div>

                  <div
                    onClick={() => setEditFormData({ ...editFormData, role: 'user' })}
                    className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                      editFormData.role === 'user'
                        ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-500/20'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <UserIcon className={`w-4 h-4 ${editFormData.role === 'user' ? 'text-blue-700' : 'text-slate-400'}`} />
                      <span className="text-xs font-black text-slate-800">Staf Konten</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Di bawah admin (Kontributor & entri data).
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Status Akun</label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-emerald-700">
                    <input
                      type="radio"
                      name="editStatus"
                      checked={editFormData.status === 'active'}
                      onChange={() => setEditFormData({ ...editFormData, status: 'active' })}
                      className="text-emerald-600"
                    />
                    <span>Aktif (Dapat Login)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-rose-700">
                    <input
                      type="radio"
                      name="editStatus"
                      checked={editFormData.status === 'inactive'}
                      onChange={() => setEditFormData({ ...editFormData, status: 'inactive' })}
                      className="text-rose-600"
                    />
                    <span>Nonaktif (Akses Dikunci)</span>
                  </label>
                </div>
              </div>

              {/* New Password (Optional) */}
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1.5">
                <label className="text-xs font-bold text-slate-700 block flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-amber-600" />
                  <span>Ganti Kata Sandi (Kosongkan jika tidak diubah)</span>
                </label>
                <input
                  type="password"
                  placeholder="Ketik kata sandi baru jika ingin mengganti..."
                  value={editFormData.newPassword}
                  onChange={(e) => setEditFormData({ ...editFormData, newPassword: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 flex items-center justify-between mb-1">
                  <span>Biografi & Catatan Penulis:</span>
                  <span className="text-[10px] text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">Tampil di Bawah Artikel & Profil</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="Contoh: Praktisi profesional dan penulis artikel dengan fokus pada inovasi, manajemen, dan pengembangan bisnis..."
                  value={editFormData.bio}
                  onChange={(e) => setEditFormData({ ...editFormData, bio: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Jika diisi, teks biografi ini akan ditampilkan pada kotak profil penulis di bagian bawah artikel yang ditulis oleh akun ini.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 block">
                  Foto Profil / Avatar Penulis
                </label>
                <div className="flex items-center gap-3">
                  <img
                    src={editFormData.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80'}
                    alt="Preview"
                    className="w-12 h-12 rounded-full object-cover border-2 border-slate-200 shadow-2xs shrink-0"
                  />
                  <div className="flex-1 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <label className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl cursor-pointer border border-slate-300 flex items-center gap-1.5 transition-colors">
                        <Upload className="w-3.5 h-3.5 text-slate-600" />
                        <span>Unggah Foto File Baru...</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, true)}
                        />
                      </label>
                    </div>
                    <input
                      type="text"
                      placeholder="Atau paste URL foto (https://...)"
                      value={editFormData.avatar}
                      onChange={(e) => setEditFormData({ ...editFormData, avatar: e.target.value })}
                      className="w-full px-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden text-slate-800"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 text-xs font-bold text-white rounded-xl shadow-md hover:opacity-95 transition-all cursor-pointer disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {submitting ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(userToDelete)}
        title="Hapus Pengguna"
        message={`Apakah Anda yakin ingin menghapus akun pengguna "${userToDelete?.name}" (@${userToDelete?.username})? Tindakan ini tidak dapat dibatalkan.`}
        confirmLabel="Ya, Hapus Pengguna"
        isDanger={true}
        onConfirm={executeDeleteUser}
        onCancel={() => setUserToDelete(null)}
      />
    </div>
  );
}
